/**
 * Trip Recording Service for FlowRide
 * Captures and buffers raw GPS and compass data for trip recording
 */

import axios from "axios";
import { estimateJsonUtf8Bytes } from "../../tools/MemoryEstimate.tools";
import { CompassData, RawGpsData } from "../../types/Navigation";
import { RecordingState, TripMetadata, TripRecordingUpload, type RecordedTripData } from "../../types/TripRecording";
import { ItineraryService } from "../ItineraryService";
import { rawCompassDataService } from "./RawCompassDataService";
import { rawGpsDataService } from "./RawGpsDataService";

/**
 * Service that manages trip recording
 */
export class TripRecordingService {

    /** Singleton instance */
    private static instance: TripRecordingService | null = null;

    /** Current recording state */
    private state: RecordingState = RecordingState.STOPPED;

    /** Buffered GPS data */
    private gpsDataBuffer: RawGpsData[] = [];

    /** Buffered compass data */
    private compassDataBuffer: CompassData[] = [];

    /** Unsubscribe function from raw GPS service */
    private unsubscribeFromRawGps: ( () => void ) | null = null;

    /** Unsubscribe function from raw compass service */
    private unsubscribeFromRawCompass: ( () => void ) | null = null;

    /** Itinerary service for reverse geocoding */
    private itineraryService: ItineraryService = new ItineraryService();

    /**
     * Private constructor for singleton pattern
     */
    private constructor() {
        // Private constructor
    }

    /**
     * Get singleton instance
     * @returns Service instance
     */
    public static getInstance(): TripRecordingService {
        if ( TripRecordingService.instance === null ) {
            TripRecordingService.instance = new TripRecordingService();
        }

        return TripRecordingService.instance;
    }

    /**
     * Start recording trip data
     */
    public start(): void {
        if ( this.state === RecordingState.RECORDING ) {
            console.warn( "[TripRecordingService] Already recording" );

            return;
        }

        // Clear buffers
        this.gpsDataBuffer = [];
        this.compassDataBuffer = [];

        // Subscribe to raw GPS data
        this.unsubscribeFromRawGps = rawGpsDataService.subscribe( ( data: RawGpsData ) => {
            this.gpsDataBuffer.push( data );
        } );

        // Subscribe to raw compass data
        this.unsubscribeFromRawCompass = rawCompassDataService.subscribe( ( data: CompassData ) => {
            this.compassDataBuffer.push( data );
        } );

        this.state = RecordingState.RECORDING;
        console.log( "[TripRecordingService] Started recording" );
    }

    /**
     * Pause recording
     */
    public pause(): void {
        if ( this.state !== RecordingState.RECORDING ) {
            console.warn( "[TripRecordingService] Not recording, cannot pause" );

            return;
        }

        this.state = RecordingState.PAUSED;
        console.log( "[TripRecordingService] Recording paused" );
    }

    /**
     * Resume recording
     */
    public resume(): void {
        if ( this.state !== RecordingState.PAUSED ) {
            console.warn( "[TripRecordingService] Not paused, cannot resume" );

            return;
        }

        this.state = RecordingState.RECORDING;
        console.log( "[TripRecordingService] Recording resumed" );
    }

    /**
     * Stop recording and save to backend
     * @returns Promise that resolves when data is saved
     */
    public async stop(): Promise<TripMetadata | null> {
        if ( this.state === RecordingState.STOPPED ) {
            console.warn( "[TripRecordingService] Not recording, cannot stop" );

            return null;
        }

        // Unsubscribe from services
        if ( this.unsubscribeFromRawGps !== null ) {
            this.unsubscribeFromRawGps();
            this.unsubscribeFromRawGps = null;
        }

        if ( this.unsubscribeFromRawCompass !== null ) {
            this.unsubscribeFromRawCompass();
            this.unsubscribeFromRawCompass = null;
        }

        // Check if we have data
        if ( this.gpsDataBuffer.length === 0 ) {
            console.warn( "[TripRecordingService] No GPS data recorded" );
            this.state = RecordingState.STOPPED;

            return null;
        }

        try {
            // Prepare upload data
            const uploadData: TripRecordingUpload = {
                "gpsData":     this.gpsDataBuffer,
                "compassData": this.compassDataBuffer,
            };

            // Send to backend
            const response = await axios.post( "/api/trip-recording/upload", uploadData );
            const savedTrip = response.data as RecordedTripData;

            // Clear buffers
            this.gpsDataBuffer = [];
            this.compassDataBuffer = [];

            this.state = RecordingState.STOPPED;
            console.log( "[TripRecordingService] Recording stopped and saved", savedTrip.metadata );

            return savedTrip.metadata;
        } catch( error ) {
            console.error( "[TripRecordingService] Error saving trip recording:", error );
            this.state = RecordingState.STOPPED;

            return null;
        }
    }

    /**
     * Extract city name from reverse geocoding response
     * @param reverseResponse - Reverse geocoding response
     * @returns City name or "Unknown"
     */
    private extractCityName( reverseResponse: Array<{
        "address":  string;
        "location":     {
            "lat": string;
            "lon": string;
        };
        "source":       string;
        "country"?:     string;
        "countryCode"?: string;
    }> ): string {
        if ( reverseResponse.length === 0 ) {
            return "Unknown";
        }

        // Try to extract city from address
        const firstResult = reverseResponse[0];
        const address = firstResult.address;

        // Look for city name in address (usually after comma)
        const parts = address.split( "," );
        if ( parts.length >= 2 ) {
            // Usually city is the second part
            return parts[1].trim();
        }

        // Fallback to full address
        return address;
    }

    /**
     * Extract city name from SearchResultItem array (from reverseGeocode)
     * @param reverseResponse - Array of SearchResultItem from reverse geocoding
     * @returns City name or "Unknown"
     */
    /**
     * Get current recording state
     * @returns Current recording state
     */
    public getState(): RecordingState {
        return this.state;
    }

    /**
     * Returns approximate in-memory size of active recording buffers.
     *
     * @returns Sample counts and serialized byte estimate for diagnostics.
     */
    public getMemoryDiagnostics(): {
        "state":                string;
        "gpsBufferSamples":     number;
        "compassBufferSamples": number;
        "estimatedBytes":       number;
    } {
        const estimatedBytes = estimateJsonUtf8Bytes( this.gpsDataBuffer ) +
          estimateJsonUtf8Bytes( this.compassDataBuffer );

        return {
            "state":                String( this.state ),
            "gpsBufferSamples":     this.gpsDataBuffer.length,
            "compassBufferSamples": this.compassDataBuffer.length,
            estimatedBytes,
        };
    }

    /**
     * Get recording statistics
     *
     * @returns Statistics about current recording
     */
    public getStatistics(): {
        "gpsDataCount":     number;
        "compassDataCount": number;
        "duration":         number;
    } {
        if ( this.gpsDataBuffer.length === 0 ) {
            return {
                "gpsDataCount":     0,
                "compassDataCount": 0,
                "duration":         0,
            };
        }

        const startTime = this.gpsDataBuffer[0].timestamp;
        const endTime = this.gpsDataBuffer[this.gpsDataBuffer.length - 1].timestamp;

        return {
            "gpsDataCount":     this.gpsDataBuffer.length,
            "compassDataCount": this.compassDataBuffer.length,
            "duration":         endTime - startTime,
        };
    }

}

/**
 * Export singleton instance for convenience
 */
export const tripRecordingService = TripRecordingService.getInstance();

